"use client";

import { Download, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { PyloMediaLightbox } from "@/components/pylo-media-lightbox";
import { MediaThumb } from "./media-thumb";
import { isUploadBusy, MediaUploadOverlay, type MediaUploadState } from "./media-upload-overlay";

export interface MediaFieldTileProps extends MediaUploadState {
  name?: string;
  previewUrl?: string;
  mimeType?: string;
  /** Lets the lightbox mint a fresh signed URL once this one expires. */
  mediaId?: string;
  className?: string;
  onRemove?: () => void;
}

const download = async (url: string, name?: string) => {
  const response = await fetch(url);
  const objectUrl = URL.createObjectURL(await response.blob());
  const anchor = document.createElement("a");
  anchor.href = objectUrl;
  anchor.download = name ?? "download";
  anchor.click();
  URL.revokeObjectURL(objectUrl);
};

export const MediaFieldTile = ({
  name,
  previewUrl,
  mimeType,
  mediaId,
  sizeBytes,
  status,
  progress,
  error,
  uploadedBytes,
  uploadSpeed,
  eta,
  className,
  onRemove,
}: MediaFieldTileProps) => {
  const upload = { status, progress, error, uploadedBytes, uploadSpeed, eta, sizeBytes };
  const isBusy = isUploadBusy(upload);

  const thumb = (
    <MediaThumb
      media={{ url: previewUrl ?? "", file_mime_type: mimeType ?? "", file_name: name }}
    />
  );

  return (
    <div
      className={cn(
        "group relative aspect-square overflow-hidden rounded-md border bg-gray-50",
        error && "border-destructive/40",
        className,
      )}
    >
      {previewUrl && !isBusy && !error ? (
        <PyloMediaLightbox
          url={previewUrl}
          mediaId={mediaId}
          fileName={name}
          mimeType={mimeType}
          thumbnailSize="full"
        >
          <div className="h-full w-full cursor-pointer">{thumb}</div>
        </PyloMediaLightbox>
      ) : (
        thumb
      )}

      <MediaUploadOverlay {...upload} />

      {!isBusy && (
        <div className="pointer-events-none absolute inset-x-0 top-0 flex items-start justify-end gap-1 bg-gradient-to-b from-black/40 to-transparent p-1.5 opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100">
          {previewUrl && !error && (
            <button
              type="button"
              title="Download"
              aria-label={`Download ${name ?? "file"}`}
              onClick={() => download(previewUrl, name)}
              className="pointer-events-auto rounded-full bg-white/90 p-1 text-gray-700 transition-colors hover:bg-white"
            >
              <Download className="h-3.5 w-3.5" />
            </button>
          )}
          {onRemove && (
            <button
              type="button"
              title="Remove"
              aria-label={`Remove ${name ?? "file"}`}
              onClick={onRemove}
              className="pointer-events-auto rounded-full bg-white/90 p-1 text-gray-700 transition-colors hover:bg-white"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      )}

      {name && (
        <div className="pointer-events-none absolute inset-x-0 bottom-0 truncate bg-gradient-to-t from-black/70 to-transparent px-1.5 pb-1 pt-4 text-[11px] text-white opacity-0 transition-opacity group-hover:opacity-100">
          {name}
        </div>
      )}
    </div>
  );
};
